import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink, RefreshCw } from 'lucide-react';

import { Migration } from '../api/migrations';
import { isTenantNotFound } from '../api/tenantRegistry';
import BprtStatusBadge from '../components/BprtStatusBadge';
import ConsentStatusChip from '../components/ConsentStatusChip';
import RelativeTime from '../components/RelativeTime';
import SectionCard from '../components/SectionCard';
import TenantLabel from '../components/TenantLabel';
import { Fact, Mono, SectionLabel } from '../components/detailPrimitives';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { useRefreshTenant, useTenantDetailQuery } from '../hooks/useTenantDetail';
import { ACCENT } from '../theme/tokens';

const useStyles = makeStyles({
  card: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    padding: '20px',
  },
  openLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '4px',
    // FD-466 — the accent TEXT rung, from the token module rather than a
    // literal. Every one of these back links carried a hardcoded hex through
    // two accent retirements (sky-700, then Rubix orange) and the drift gate
    // caught them both times; taking the value from `ACCENT` is what stops a
    // third.
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
});

// In-migration tenant surface (FD-160, ADR-023). Shows the migration's linked
// source/destination tenants (consent / SP / SA readiness, with Trigger-consent
// + Re-check inline and a link out to the tenant detail) plus the wave BPRT
// credential status stamped on the migration record.
export default function MigrationTenantsPage() {
  const { migrationId } = useParams<{ migrationId: string }>();
  const query = useMigrationDetailQuery(migrationId);
  const migration = query.data?.migration;

  if (!migration) return null;

  const slots: { role: 'source' | 'destination'; tenantId: string | null; domain: string | null }[] = [
    { role: 'source', tenantId: migration.sourceTenantId, domain: migration.sourceTenantDomain },
    { role: 'destination', tenantId: migration.destTenantId, domain: migration.destTenantDomain },
  ];
  const linkedSlots = slots.filter((s) => Boolean(s.tenantId));

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-10"
    >
      {/* Connected tenants */}
      <section>
        <SectionLabel>Connected tenants</SectionLabel>
        {linkedSlots.length === 0 ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>No linked source or destination tenants</MessageBarTitle>
              This migration has no explicit source/destination tenant recorded (typical for a
              D2C campaign, which joins the deployment&apos;s own tenant). Wave credential status
              is shown below.
            </MessageBarBody>
          </MessageBar>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {linkedSlots.map((s) => (
              <MigrationTenantCard
                key={s.role}
                role={s.role}
                tenantId={s.tenantId as string}
                domain={s.domain}
              />
            ))}
          </div>
        )}
      </section>

      {/* Wave credential (BPRT) — from the migration record */}
      <section>
        <SectionLabel>Wave credential (BPRT)</SectionLabel>
        <SectionCard>
          <WaveBprt migration={migration} />
        </SectionCard>
      </section>
    </motion.div>
  );
}

function WaveBprt({ migration }: { migration: Migration }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-5">
      <Fact label="Status">
        <BprtStatusBadge status={migration.bprtStatus} />
      </Fact>
      <Fact label="Expires">
        <span className="text-zinc-800">
          <RelativeTime iso={migration.bprtExpiresUtc} emptyPlaceholder="—" />
        </span>
      </Fact>
      <Fact label="Package user">
        {migration.bprtPackagePid ? (
          <Mono>{migration.bprtPackagePid}</Mono>
        ) : (
          <span className="text-zinc-400">—</span>
        )}
      </Fact>
    </dl>
  );
}

function MigrationTenantCard({
  role,
  tenantId,
  domain,
}: {
  role: 'source' | 'destination';
  tenantId: string;
  domain: string | null;
}) {
  const styles = useStyles();
  const query = useTenantDetailQuery(tenantId);
  const refresh = useRefreshTenant(tenantId);
  const roleLabel = role === 'source' ? 'Source' : 'Destination';

  const isNewTenant = query.isError && isTenantNotFound(query.error);
  const tenant = query.data;
  const canTriggerConsent =
    Boolean(tenant) && tenant!.consentStatus !== 'consented' && Boolean(tenant!.consentUrl);

  return (
    <div className={styles.card}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex flex-col gap-1 min-w-0">
          <span className="font-display text-[11px] font-medium uppercase tracking-[0.06em] text-zinc-400">
            {roleLabel} tenant
          </span>
          <span className="font-display font-medium text-zinc-900">
            <TenantLabel domain={domain} tenantId={tenantId} />
          </span>
        </div>
        <Link to={`/tenants/${encodeURIComponent(tenantId)}/overview`} className={styles.openLink}>
          Open tenant
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </Link>
      </div>

      {query.isLoading ? (
        <span className="inline-flex items-center gap-2 text-zinc-500 text-sm">
          <Spinner size="tiny" /> Loading tenant…
        </span>
      ) : isNewTenant ? (
        <span className="text-sm text-zinc-500">
          Not yet in the tenant registry — it is registered when the migration is prepared.
        </span>
      ) : tenant ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <ConsentStatusChip status={tenant.consentStatus} />
            {canTriggerConsent && (
              <Button
                size="small"
                appearance="primary"
                icon={<ExternalLink size={14} strokeWidth={1.5} />}
                as="a"
                href={tenant.consentUrl as string}
                target="_blank"
                rel="noreferrer"
              >
                Trigger consent
              </Button>
            )}
            <Button
              size="small"
              appearance="subtle"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? 'Re-checking…' : 'Re-check'}
            </Button>
          </div>

          <dl className="flex flex-col gap-2.5 text-sm border-t border-zinc-100 pt-3">
            <CardFact label="Tenant app">
              {tenant.servicePrincipals.length > 0 ? (
                <Badge appearance="tint" color="success">
                  Present
                </Badge>
              ) : (
                <Badge appearance="tint" color="warning">
                  Not found
                </Badge>
              )}
            </CardFact>
            {role === 'destination' && (
              <CardFact label="Minting account">
                {tenant.bprtServiceAccount?.present ? (
                  <Badge appearance="tint" color="success">
                    Configured
                  </Badge>
                ) : (
                  <Badge appearance="tint" color="subtle">
                    Not configured
                  </Badge>
                )}
              </CardFact>
            )}
            <CardFact label="Checked">
              <span className="text-zinc-700">
                <RelativeTime iso={tenant.refreshedUtc} emptyPlaceholder="never" />
              </span>
            </CardFact>
          </dl>
        </div>
      ) : (
        <span className="text-sm text-zinc-500">Tenant details are temporarily unavailable.</span>
      )}
    </div>
  );
}

function CardFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="font-display text-zinc-500">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
